import { useEffect, useRef } from "react";
import { useLazyImport } from "@culturehq/hooks";

import Sortable, { Options, SortableEvent } from "sortablejs";

type UseSortableOpts = Omit<Options, "onEnd" | "onSort"> & {
  onSort: (_event: SortableEvent) => Promise<any>;
};

const useSortable = ({ onSort, ...opts }: UseSortableOpts) => {
  const SortableJS = useLazyImport(() => import("sortablejs"));
  const sortableRef = useRef<HTMLElement>(null);

  // Purposefully leaving out the deps array here. The issue is that we want to
  // only run this once the ref has been filled, but refs don't trigger
  // rerenders so `useEffect` won't pick it up. In this case this function is
  // pretty inexpensive so it's okay so just recreate the sortable.
  useEffect(() => {
    let sortable: Sortable;

    if (SortableJS && sortableRef.current) {
      sortable = new SortableJS(sortableRef.current, {
        ...opts,
        onEnd: (event: SortableEvent) => {
          sortable.option("disabled", true);

          const onSorted = () => sortable.option("disabled", false);
          onSort(event).then(onSorted).catch(onSorted);
        }
      });
    }

    return () => sortable && sortable.destroy();
  });

  return sortableRef;
};

export default useSortable;
